import {
  type RemixiconComponentType,
  RiAlertLine,
  RiCheckLine,
  RiEyeOffLine,
  RiFileListLine,
  RiInformationLine,
  RiNotification3Line,
  RiStarLine,
  RiTelegram2Line,
  RiVolumeUpLine,
} from "@remixicon/react";
import type { AlertRuleSpec, AlertRuleView, MarketKind, SideView } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldError, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { LockedInline } from "@screener/uikit/components/locked-inline";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { SegmentedControl } from "@screener/uikit/components/segmented-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@screener/uikit/components/select";
import { Sheet, SheetContent, SheetTitle } from "@screener/uikit/components/sheet";
import { Switch } from "@screener/uikit/components/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { exchangeOptions } from "@/lib/exchange-options";
import { actionLabel, exchangeLabel } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import {
  emptyRuleForm,
  formFromRule,
  type RuleForm,
  type RuleFormErrors,
  specFromForm,
  validateRuleForm,
} from "./rule-form";
import {
  mmFilterFieldLabel,
  QUICK_VOLUMES,
  quickVolumeSelected,
  ruleSummary,
  TRIGGERS,
  toggleQuickVolume,
  volumeFieldHint,
} from "./rule-labels";
import { triggerInfo } from "./rule-list";
import type { AlertRuleWriteOutcome } from "./use-alert-rules";

/**
 * Форма правила экрана «Алерты» (spec `web-alerts-screen`, design решение 4,
 * tasks.md 6.3–6.4). `RuleFormContent` — чистая разметка (см.
 * `rule-form-sheet.test.tsx`); `RuleFormSheet` держит состояние формы и
 * запись поверх `Sheet` (образец — `apps/web/src/density/density-detail-panel.tsx`).
 */

/** Отказ последней попытки сохранить — различается текстом (спека «Форма не отправляет…»). */
export type RuleSubmitError = "unavailable" | "invalid" | "not_found";

/**
 * Отвечает, закрывать ли форму по запросу `onOpenChange` `Sheet` (Esc,
 * клик по подложке, крестик) — не закрывать, пока форма пишет свою попытку
 * (ревью stage6-alert-web, пункт A.1).
 */
export function nextOpenChangeShouldClose(requestedOpen: boolean, submitting: boolean): boolean {
  return !requestedOpen && !submitting;
}

/**
 * Ответ записи применяется, только если он относится к попытке текущего
 * открытия формы: `RuleFormSheet` увеличивает поколение на каждый фронт
 * `open` (ревью, пункт A.1) — иначе ответ уже закрытой или переоткрытой
 * формы применился бы к чужому черновику.
 */
export function isCurrentAttempt(attemptGeneration: number, openGeneration: number): boolean {
  return attemptGeneration === openGeneration;
}

/** Событие, после которого решается судьба отказа последней записи. */
export type RuleFormErrorEvent = "field_change" | "submit_attempt" | "close";

/**
 * Отказ последней записи (`submitError`) не гаснет от любой правки поля —
 * иначе форма выглядела бы так, будто причина устранена, хотя пользователь
 * ничего не менял по существу (ревью stage6-alert-web, второй круг, пункт
 * 5). Гаснет он ровно в двух случаях: следующая попытка сохранения (человек
 * решил, что поправил) и закрытие формы (отказ не переживает саму форму).
 */
export function nextSubmitError(
  current: RuleSubmitError | null,
  event: RuleFormErrorEvent,
): RuleSubmitError | null {
  return event === "field_change" ? current : null;
}

/** Рынок и сторона — сегменты с пунктом «любой»/«обе»: пустое значение формы — «любое» (§6.5 1.20). */
const MARKET_OPTIONS: readonly { value: "" | MarketKind; label: string }[] = [
  { value: "", label: "Любой" },
  { value: "spot", label: "Spot" },
  { value: "perp", label: "Perp" },
];

const SIDE_OPTIONS: readonly { value: "" | SideView; label: string }[] = [
  { value: "", label: "Обе" },
  { value: "bid", label: "Bid" },
  { value: "ask", label: "Ask" },
];

/** Сегмент высотой 30 px, как поля рядом: пункты тянутся на всю ширину ячейки. */
const SEGMENT_CLASS = "h-[30px] w-full [&>button]:h-[26px] [&>button]:flex-1 [&>button]:text-xs";
const FIELD_CLASS = "h-[30px] text-[13px] md:text-[13px]";

interface RuleFormContentProps {
  form: RuleForm;
  errors: RuleFormErrors;
  /** Биржи для выбора — `exchangeOptions(exchanges, form.exchange)`, посчитанные вызывающей стороной. */
  exchangeChoices: readonly string[];
  telegramConfigured: boolean | null;
  submitError: RuleSubmitError | null;
  submitting: boolean;
  /** Где-то на экране уже идёт другая запись (§ ревью A.2) — блокирует «Сохранить», не «Отмена». */
  writing: boolean;
  /** Текст отказа записи — фокус уходит сюда при отказе (ревью, второй круг, пункт 3). */
  errorRef?: (el: HTMLParagraphElement | null) => void;
  onChange: (patch: Partial<RuleForm>) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function submitErrorText(error: RuleSubmitError): string {
  switch (error) {
    case "unavailable":
      return "Демон недоступен — правило не записано.";
    case "invalid":
      return "Демон не принял правило — проверьте поля.";
    case "not_found":
      return "Правило уже удалено в другой вкладке.";
    default:
      return error satisfies never;
  }
}

interface FormSectionProps {
  title: string;
  /** Справа в заголовке раздела — приглушённая подсказка. */
  aside?: ReactNode;
  /** Рядом с заголовком — например, значок подсказки. */
  titleAddon?: ReactNode;
  last?: boolean;
  children: ReactNode;
}

/** Раздел формы: заголовок-капитель, отступы 14/16/16 и линия снизу (макет 1d). */
function FormSection({ title, aside, titleAddon, last = false, children }: FormSectionProps) {
  return (
    <section
      className={cn("flex flex-col gap-2.5 px-4 pt-3.5 pb-4", !last && "border-b border-border")}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <SectionLabel as="h3">{title}</SectionLabel>
        {titleAddon}
        {aside !== undefined ? (
          <span className="ml-auto text-[11px] text-muted-foreground">{aside}</span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

interface SwitchRowProps {
  id: string;
  Icon: RemixiconComponentType;
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Замок тарифа: переключатель выключен и не нажимается, рядом замок; значение формы не трогается. */
  lock?: { label: string; text: string };
}

/** Строка пометки или действия: плашка значка 28 px, название, пояснение и `Switch` (макет 1d). */
function SwitchRow({
  id,
  Icon,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
  lock,
}: SwitchRowProps) {
  const locked = lock !== undefined;
  const shown = checked && !locked;
  return (
    <div className="flex min-h-10 items-center gap-2.5">
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary",
          shown ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span id={`${id}-title`} className="text-[13px] leading-snug font-medium">
          {title}
        </span>
        <span id={`${id}-description`} className="text-[11px] leading-snug text-muted-foreground">
          {description}
        </span>
      </div>
      {lock === undefined ? null : <LockedInline label={lock.label} text={lock.text} />}
      <Switch
        checked={shown}
        disabled={disabled || locked}
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

/** Разметка формы без `Sheet` — тестируется `renderToStaticMarkup` напрямую. */
export function RuleFormContent({
  form,
  errors,
  exchangeChoices,
  telegramConfigured,
  submitError,
  submitting,
  writing,
  errorRef,
  onChange,
  onCancel,
  onSubmit,
}: RuleFormContentProps) {
  const planGate = usePlanGate();
  const selectedTrigger = triggerInfo(form.trigger);
  const hasErrors = Object.keys(errors).length > 0;
  const saveDisabled = hasErrors || submitting || writing;
  const quickSelected = quickVolumeSelected(form);
  // Подпись выбранной биржи резолвится из `items`, пока список не открывался
  // (@base-ui/react/select берёт подпись из `items`, не только из смонтированных `SelectItem`).
  const exchangeItems: Record<string, string> = {
    "": "Любая биржа",
    ...Object.fromEntries(exchangeChoices.map((exchange) => [exchange, exchangeLabel(exchange)])),
  };
  const volumeHint = volumeFieldHint(form.trigger);

  // Стрелки двигают выбор события, как у радиогруппы (APG): фокус и значение вместе.
  function handleTriggerKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0 || submitting) {
      return;
    }
    event.preventDefault();
    const index = TRIGGERS.findIndex((entry) => entry.value === form.trigger);
    const next = TRIGGERS[(index + step + TRIGGERS.length) % TRIGGERS.length];
    if (next === undefined) {
      return;
    }
    onChange({ trigger: next.value });
    event.currentTarget.querySelector<HTMLElement>(`[data-event="${next.value}"]`)?.focus();
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <FormSection title="Событие">
          <div
            role="radiogroup"
            aria-label="Событие"
            onKeyDown={handleTriggerKeyDown}
            className="grid grid-cols-2 gap-1 min-[460px]:grid-cols-4"
          >
            {TRIGGERS.map((entry) => {
              const selected = entry.value === form.trigger;
              return (
                <Button
                  key={entry.value}
                  variant="toggle"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={selected ? 0 : -1}
                  data-event={entry.value}
                  disabled={submitting}
                  onClick={() => onChange({ trigger: entry.value })}
                  className="h-[30px] min-w-0 justify-between gap-1 rounded-md pr-2 pl-2.5 text-xs font-medium"
                >
                  <span className="min-w-0 truncate">{entry.label}</span>
                  {selected ? <RiCheckLine aria-hidden="true" className="size-3 shrink-0" /> : null}
                </Button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{selectedTrigger.label}</span> —{" "}
            {selectedTrigger.hint}
          </p>
        </FormSection>

        <FormSection title="Фильтр" aside="пустое поле — любое значение">
          <div className="grid grid-cols-2 gap-2">
            <Input
              id="rule-form-base"
              aria-label="Монета"
              value={form.base}
              placeholder="Монета"
              autoComplete="off"
              disabled={submitting}
              className={FIELD_CLASS}
              onChange={(event) => onChange({ base: event.target.value })}
            />
            <Select<string>
              items={exchangeItems}
              value={form.exchange ?? ""}
              onValueChange={(value) =>
                onChange({ exchange: value === "" || value === null ? null : value })
              }
              disabled={submitting}
            >
              <SelectTrigger
                id="rule-form-exchange"
                aria-label="Биржа"
                className="w-full rounded-md text-[13px] data-[size=default]:h-[30px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Любая биржа</SelectItem>
                {exchangeChoices.map((exchange) => (
                  <SelectItem key={exchange} value={exchange}>
                    {exchangeLabel(exchange)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SegmentedControl<"" | MarketKind>
              label="Рынок"
              size="sm"
              className={SEGMENT_CLASS}
              options={MARKET_OPTIONS.map((option) => ({ ...option, disabled: submitting }))}
              value={form.market ?? ""}
              onValueChange={(market) => onChange({ market: market === "" ? null : market })}
            />
            <SegmentedControl<"" | SideView>
              label="Сторона"
              size="sm"
              className={SEGMENT_CLASS}
              options={SIDE_OPTIONS.map((option) => ({ ...option, disabled: submitting }))}
              value={form.side ?? ""}
              onValueChange={(side) => onChange({ side: side === "" ? null : side })}
            />
          </div>
        </FormSection>

        <FormSection
          title="Размер"
          titleAddon={
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="О размере"
                    className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  />
                }
              >
                <RiInformationLine aria-hidden="true" className="size-3" />
              </TooltipTrigger>
              <TooltipContent>{volumeHint}</TooltipContent>
            </Tooltip>
          }
          aside={volumeHint}
        >
          <div className="grid grid-cols-[1fr_10px_1fr] items-start gap-x-1.5">
            <Field invalid={Boolean(errors.minUsd)} className="gap-1">
              <div className="relative">
                <FieldLabel htmlFor="rule-form-min-usd" className="sr-only">
                  от, USD
                </FieldLabel>
                <Input
                  id="rule-form-min-usd"
                  inputMode="text"
                  value={form.minUsd}
                  placeholder="от"
                  disabled={submitting}
                  className={cn(FIELD_CLASS, "pr-11 tabular-nums")}
                  onChange={(event) => onChange({ minUsd: event.target.value })}
                />
                <UsdSuffix />
              </div>
              {errors.minUsd ? <FieldError match>{errors.minUsd}</FieldError> : null}
            </Field>
            <span aria-hidden="true" className="pt-1.5 text-center text-muted-foreground">
              −
            </span>
            <Field invalid={Boolean(errors.maxUsd)} className="gap-1">
              <div className="relative">
                <FieldLabel htmlFor="rule-form-max-usd" className="sr-only">
                  до, USD
                </FieldLabel>
                <Input
                  id="rule-form-max-usd"
                  inputMode="text"
                  value={form.maxUsd}
                  placeholder="до"
                  disabled={submitting}
                  className={cn(FIELD_CLASS, "pr-11 tabular-nums")}
                  onChange={(event) => onChange({ maxUsd: event.target.value })}
                />
                <UsdSuffix />
              </div>
              {errors.maxUsd ? <FieldError match>{errors.maxUsd}</FieldError> : null}
            </Field>
          </div>
          {errors.range ? (
            <p role="alert" className="text-sm text-destructive">
              {errors.range}
            </p>
          ) : null}
          {/* biome-ignore lint/a11y/useSemanticElements: ряд — сетка; у <fieldset> свои правила раскладки, имя группе даёт aria-label */}
          <div role="group" aria-label="Быстрые значения «от»" className="grid grid-cols-5 gap-1">
            {QUICK_VOLUMES.map((quick) => (
              <Button
                key={quick.value}
                variant="toggle"
                size="xs"
                aria-pressed={quickSelected === quick.value}
                disabled={submitting}
                className="rounded-sm px-0 text-xs font-medium tabular-nums"
                onClick={() => onChange({ minUsd: toggleQuickVolume(form.minUsd, quick.value) })}
              >
                {quick.label}
              </Button>
            ))}
          </div>
        </FormSection>

        <FormSection title="Пометки">
          <SwitchRow
            id="rule-form-favorite"
            Icon={RiStarLine}
            title="Только избранные"
            description="Монеты со звёздочкой"
            checked={form.favoriteOnly}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ favoriteOnly: checked })}
          />
          <SwitchRow
            id="rule-form-exclude-mm"
            Icon={RiEyeOffLine}
            title="Без ММ"
            description={mmFilterFieldLabel(form.trigger)}
            checked={form.excludeMm}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ excludeMm: checked })}
            lock={
              planGate.mmApproachesLocked
                ? { label: "Без ММ", text: planGate.mmApproachesText }
                : undefined
            }
          />
        </FormSection>

        <FormSection title="Действия" last>
          <SwitchRow
            id="rule-form-sound"
            Icon={RiVolumeUpLine}
            title={actionLabel("sound")}
            description="сигнал во вкладке"
            checked={form.sound}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ sound: checked })}
          />
          <SwitchRow
            id="rule-form-notify"
            Icon={RiNotification3Line}
            title={actionLabel("notify")}
            description="системное уведомление браузера"
            checked={form.notify}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ notify: checked })}
          />
          <SwitchRow
            id="rule-form-telegram"
            Icon={RiTelegram2Line}
            title={actionLabel("telegram")}
            description="сообщение в бот"
            checked={form.telegram}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ telegram: checked })}
          />
          <SwitchRow
            id="rule-form-log"
            Icon={RiFileListLine}
            title={actionLabel("log")}
            description="запись в лог"
            checked={form.log}
            disabled={submitting}
            onCheckedChange={(checked) => onChange({ log: checked })}
          />
          {errors.actions ? (
            <p role="alert" className="text-sm text-destructive">
              {errors.actions}
            </p>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            Звук и нотификация работают, пока открыта вкладка.
          </p>
          {form.telegram && telegramConfigured === false ? (
            <p className="flex items-center gap-1 text-xs text-warning">
              <RiAlertLine className="size-3.5" aria-hidden="true" />
              Бот Telegram не задан — это действие будет пропущено.
            </p>
          ) : null}
        </FormSection>
      </div>

      <div className="flex shrink-0 flex-col gap-1.5 border-t border-border px-4 py-2.5 text-xs leading-[1.45]">
        {submitError ? (
          <p
            role="alert"
            tabIndex={-1}
            ref={errorRef}
            className="text-sm text-destructive outline-none"
          >
            {submitErrorText(submitError)}
          </p>
        ) : null}
        <div className="flex items-baseline gap-3">
          <SectionLabel className="shrink-0">Итог</SectionLabel>
          <p className="min-w-0 flex-1">{ruleSummary(form)}</p>
        </div>
      </div>

      <div className="flex h-12 shrink-0 items-center justify-end gap-1.5 border-t border-border px-4">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="h-[30px] rounded-md text-xs font-medium"
        >
          Отмена
        </Button>
        <Button
          onClick={onSubmit}
          disabled={saveDisabled}
          className="h-[30px] rounded-md text-xs font-medium"
        >
          {submitting ? "Сохраняем…" : "Сохранить правило"}
        </Button>
      </div>
    </div>
  );
}

/** Суффикс «USD» поверх поля суммы — подпись валюты, не часть значения. */
function UsdSuffix() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[11px] text-muted-foreground uppercase"
    >
      USD
    </span>
  );
}

export interface RuleFormSheetProps {
  open: boolean;
  mode: "create" | "edit";
  /** Правило правки — `null` в режиме заведения. */
  initialRule: AlertRuleView | null;
  /** Биржи потока §7.6 — варианты биржи (design.md решение 4). */
  exchanges: readonly string[];
  telegramConfigured: boolean | null;
  /** Где-то на экране уже идёт другая запись контроллера правил (ревью, пункт A.2). */
  writing: boolean;
  onClose: () => void;
  /** Пишет правило через контроллер экрана; возвращает исход записи. */
  onSubmit: (spec: AlertRuleSpec) => Promise<AlertRuleWriteOutcome>;
}

function initialFormFor(mode: "create" | "edit", initialRule: AlertRuleView | null): RuleForm {
  return mode === "edit" && initialRule !== null ? formFromRule(initialRule.rule) : emptyRuleForm();
}

/**
 * Оболочка `Sheet` вокруг `RuleFormContent`: держит черновик формы, ошибки
 * поля и отказ последней записи. Форма сбрасывается заново каждый раз, когда
 * `open` переходит в `true` — с тем же значением полей, что уже стояло при
 * открытии (режим правки) или пустыми (режим заведения).
 */
export function RuleFormSheet({
  open,
  mode,
  initialRule,
  exchanges,
  telegramConfigured,
  writing,
  onClose,
  onSubmit,
}: RuleFormSheetProps) {
  const [form, setForm] = useState<RuleForm>(() => initialFormFor(mode, initialRule));
  const [submitError, setSubmitError] = useState<RuleSubmitError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Поколение открытия растёт на каждый фронт `open` — ответ записи,
  // относящийся к чужому (уже закрытому или переоткрытому) открытию формы,
  // не применяется (`isCurrentAttempt`, ревью stage6-alert-web, пункт A.1).
  const openGenerationRef = useRef(0);

  // При отказе записи фокус уходит на текст ошибки (ревью, второй круг,
  // пункт 3) — кнопка «Сохранить» на миг становится недоступна
  // (`submitting`), браузер снимает с неё фокус в `document.body`, и молча
  // он туда не возвращается.
  const errorTextRef = useRef<HTMLParagraphElement | null>(null);
  const lastSubmitErrorRef = useRef<RuleSubmitError | null>(null);
  useEffect(() => {
    if (submitError !== null && submitError !== lastSubmitErrorRef.current) {
      errorTextRef.current?.focus();
    }
    lastSubmitErrorRef.current = submitError;
  }, [submitError]);

  // Форма сбрасывается заново на каждое открытие — иначе правка одного
  // правила показывала бы черновик предыдущего открытия (спека «В режиме
  // правки форма SHALL открываться заполненной значениями правила»).
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс должен идти строго по фронту `open`, а не при каждой смене mode/initialRule во время открытой формы.
  useEffect(() => {
    if (open) {
      openGenerationRef.current += 1;
      setForm(initialFormFor(mode, initialRule));
      setSubmitError(null);
      setSubmitting(false);
    } else {
      // Отказ не переживает закрытие формы (ревью, второй круг, пункт 5,
      // `nextSubmitError`) — иначе следующее открытие на миг показало бы
      // чужую ошибку до собственного сброса выше.
      setSubmitError((current) => nextSubmitError(current, "close"));
    }
  }, [open]);

  const errors = validateRuleForm(form);

  const handleChange = (patch: Partial<RuleForm>) => {
    setForm((previous) => ({ ...previous, ...patch }));
    // Отказ последней записи гаснет по следующей попытке сохранения или
    // закрытию формы, не по правке поля (ревью, второй круг, пункт 5,
    // `nextSubmitError`) — иначе форма выглядела бы так, будто причина
    // отказа уже устранена.
  };

  const handleSubmit = () => {
    if (Object.keys(errors).length > 0 || submitting || writing) {
      return;
    }
    const result = specFromForm(form);
    if (!result.ok) {
      // `validateRuleForm`/`specFromForm` согласованы — сюда попадать не должны,
      // но лишняя проверка дешевле незаметно отправленного неполного тела.
      return;
    }
    const generation = openGenerationRef.current;
    setSubmitError((current) => nextSubmitError(current, "submit_attempt"));
    setSubmitting(true);
    onSubmit(result.spec)
      .then((outcome) => {
        if (!isCurrentAttempt(generation, openGenerationRef.current)) {
          // Форма закрылась и переоткрылась (своя или чужая попытка), пока
          // ответ был в пути — он больше не про эту форму.
          return;
        }
        if (outcome.status === "ok") {
          onClose();
          return;
        }
        if (outcome.status === "busy") {
          // Уже идёт другая попытка — эта нажата раньше, чем кнопка
          // недоступна не стала: молча ждём предыдущую, отдельного текста
          // не показываем (спека «Сохранение недоступно, пока предыдущая
          // попытка не получила ответа»).
          return;
        }
        if (outcome.status === "unauthorized") {
          // Каркас уводит на форму входа по `onSessionInvalid` контроллера —
          // здесь отдельного текста не нужно.
          return;
        }
        setSubmitError(outcome.status);
      })
      .finally(() => {
        if (isCurrentAttempt(generation, openGenerationRef.current)) {
          setSubmitting(false);
        }
      });
  };

  const handleOpenChange = (next: boolean) => {
    if (nextOpenChangeShouldClose(next, submitting)) {
      onClose();
    }
  };

  const exchangeChoices = exchangeOptions(exchanges, form.exchange);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className="flex flex-col gap-0 p-0 data-[side=right]:w-[min(460px,100vw)] data-[side=right]:sm:max-w-none"
        closeDisabled={submitting}
      >
        <div className="flex h-11 shrink-0 items-center border-b border-border pr-12 pl-4">
          <SheetTitle className="text-[15px] font-semibold">
            {mode === "edit" ? "Изменить правило" : "Новое правило"}
          </SheetTitle>
        </div>
        <RuleFormContent
          form={form}
          errors={errors}
          exchangeChoices={exchangeChoices}
          telegramConfigured={telegramConfigured}
          submitError={submitError}
          submitting={submitting}
          writing={writing}
          errorRef={(el) => {
            errorTextRef.current = el;
          }}
          onChange={handleChange}
          onCancel={onClose}
          onSubmit={handleSubmit}
        />
      </SheetContent>
    </Sheet>
  );
}
